import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getTaskAudio } from '../db';
import { setBoundaries } from '../services/boundaryEdits';
import { decodeAudioBlob, extractMono } from '../services/audioSlicer';
import { computePeaks, peakAmplitude, tickStepFor, type Peaks } from './wavePeaks';
import { useRangePlayer } from './useRangePlayer';
import { Button } from './Button';
import { round3 } from '../lib/num';

/**
 * Waveform trim editor — "the cut is wrong, let me show you where the word is".
 *
 * Shows the segment plus one second of context on each side (clamped to the
 * file), drawn as a hand-rolled min/max canvas waveform, with the segment span
 * highlighted between two draggable handles. Saving adopts the new boundaries:
 * the segment row moves, the stored clip WAV is re-cut from the ORIGINAL
 * decoded audio through the same `sliceToWav` path the first pass uses, and
 * `confidence` is pinned to 1 — a human placed these boundaries, that is ground
 * truth. The verify state is deliberately untouched: the user still has to
 * judge the label.
 *
 * Everything is per-open: the blob is read and decoded on mount and dropped on
 * unmount, so a long verify session never accumulates decoded files.
 */

/** The bit of a Segment this component needs. Deliberately not `Segment`. */
export interface WaveTrimmerTarget {
  id: string;
  taskId: string;
  startSec: number;
  endSec: number;
  heard?: string;
}

export interface WaveTrimmerProps {
  segment: WaveTrimmerTarget;
  /** Text shown above the waveform. Defaults to the segment's `heard`. */
  label?: string;
  /** Fired after the new boundaries are persisted. */
  onSaved?: (next: { startSec: number; endSec: number }) => void;
  /** Close the editor. Called on Cancel and shortly after a successful save. */
  onClose: () => void;
}

/** Seconds of lead-in / tail shown around the segment. */
const CONTEXT_SEC = 1;
/** Handles may never come closer than this. */
const MIN_SEC = 0.06;
/** Canvas height in CSS pixels. */
const WAVE_H = 112;
/** How long the "Saved" state shows before the editor closes itself. */
const SAVED_MS = 850;

interface Span {
  start: number;
  end: number;
}

type HandleId = 'start' | 'end';

/**
 * DELIBERATELY NOT `lib/num.clamp`.
 *
 * The shared one is `v < lo ? lo : v > hi ? hi : v` with no inverted-range
 * guard; this one answers `lo` whenever `hi < lo`. The two disagree for any
 * `v > hi` in an inverted range, and this file clamps against window bounds
 * (`winStart`/`winEnd`, `prev.end - MIN_SEC`, …) that are not provably ordered
 * the way `laneMove`'s deltas are. Swapping it in would be a behavior change on
 * a phone-critical editing path, so the local copy stays until someone proves
 * every one of the nine call sites below keeps `lo <= hi`.
 *
 * `laneMove.ts` DID drop its identical copy — there the single call site is
 * provably ordered. See the note on `clampDelta` there.
 */
function clamp(v: number, lo: number, hi: number): number {
  if (hi < lo) return lo;
  return v < lo ? lo : v > hi ? hi : v;
}

function cssVar(el: Element | null, name: string, fallback: string): string {
  if (!el) return fallback;
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return v || fallback;
}

export function WaveTrimmer({ segment, label, onSaved, onClose }: WaveTrimmerProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  /** The decoded ORIGINAL task audio. Released on unmount. */
  const bufferRef = useRef<AudioBuffer | null>(null);

  const [blob, setBlob] = useState<Blob | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [win, setWin] = useState<Span>({ start: 0, end: 0 });
  const [sel, setSel] = useState<Span>({ start: segment.startSec, end: segment.endSec });
  const [peaks, setPeaks] = useState<Peaks | null>(null);
  const [cssWidth, setCssWidth] = useState(0);
  const [dragging, setDragging] = useState<HandleId | null>(null);
  const [focused, setFocused] = useState<HandleId | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const draggingRef = useRef<HandleId | null>(null);
  draggingRef.current = dragging;
  /** Live playhead in original-file seconds, or null. Drawn without re-rendering. */
  const playheadRef = useRef<number | null>(null);

  const player = useRangePlayer(blob);

  /* ---------------- load + decode once per open ---------------- */

  /**
   * The boundaries the editor OPENED with. Saving writes new ones back to the
   * segment the caller is holding, and re-deriving the window from those would
   * re-decode the file and yank the handles out from under the success state.
   */
  const openedWith = useRef({ id: segment.id, start: segment.startSec, end: segment.endSec });
  if (openedWith.current.id !== segment.id) {
    openedWith.current = { id: segment.id, start: segment.startSec, end: segment.endSec };
  }

  useEffect(() => {
    let cancelled = false;
    const opened = openedWith.current;

    (async () => {
      try {
        const audio = await getTaskAudio(segment.taskId);
        if (cancelled) return;
        if (!audio) {
          setError('The original audio for this task is no longer stored.');
          setStatus('error');
          return;
        }
        const buffer = await decodeAudioBlob(audio);
        if (cancelled) return;

        bufferRef.current = buffer;
        const winStart = Math.max(0, opened.start - CONTEXT_SEC);
        const winEnd = Math.min(buffer.duration, opened.end + CONTEXT_SEC);
        setWin({ start: winStart, end: winEnd });
        // A stored segment can sit slightly outside its own window only if the
        // file was replaced under it; clamp so the handles are always reachable.
        setSel({
          start: clamp(opened.start, winStart, winEnd),
          end: clamp(opened.end, winStart, winEnd),
        });
        setBlob(audio);
        setStatus('ready');
      } catch (err: unknown) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not decode this task audio.');
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      // release the decoded file — nothing is cached globally
      bufferRef.current = null;
    };
  }, [segment.id, segment.taskId]);

  /* ---------------- track the canvas width ---------------- */

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setCssWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [status]);

  /* ---------------- peaks for the window ---------------- */

  useEffect(() => {
    const buffer = bufferRef.current;
    if (!buffer || cssWidth <= 0 || win.end <= win.start) return;
    const dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
    const mono = extractMono(buffer, win.start, win.end);
    setPeaks(computePeaks(mono, Math.round(cssWidth * dpr)));
  }, [cssWidth, win.start, win.end, status]);

  /* ---------------- drawing ---------------- */

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !peaks || cssWidth <= 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
    const w = Math.round(cssWidth * dpr);
    const h = Math.round(WAVE_H * dpr);
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;

    const root = canvas;
    const accent = cssVar(root, '--accent', '#6ee7b7');
    const dim = cssVar(root, '--text-faint', '#6b7688');
    const line = cssVar(root, '--border', '#2b3342');
    const warn = cssVar(root, '--warn', '#fbbf24');

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const span = Math.max(1e-6, win.end - win.start);
    const xOf = (t: number) => ((t - win.start) / span) * w;
    const selX0 = xOf(sel.start);
    const selX1 = xOf(sel.end);

    // selection band
    ctx.globalAlpha = 0.14;
    ctx.fillStyle = accent;
    ctx.fillRect(selX0, 0, Math.max(1, selX1 - selX0), h);
    ctx.globalAlpha = 1;

    // waveform, drawn one column per device pixel
    const mid = h / 2;
    const amp = peakAmplitude(peaks);
    // scale quiet material up, but never beyond 6x, and leave 12% headroom
    const gain = amp > 0 ? Math.min(6, 0.88 / amp) : 1;
    const half = mid - 8 * dpr;
    const cols = Math.min(peaks.columns, w);

    for (let c = 0; c < cols; c++) {
      const lo = clamp(peaks.min[c] * gain, -1, 1);
      const hi = clamp(peaks.max[c] * gain, -1, 1);
      const y0 = mid - hi * half;
      const y1 = mid - lo * half;
      const inSel = c + 0.5 >= selX0 && c + 0.5 <= selX1;
      ctx.fillStyle = inSel ? accent : dim;
      ctx.globalAlpha = inSel ? 0.95 : 0.5;
      ctx.fillRect(c, y0, 1, Math.max(dpr, y1 - y0));
    }
    ctx.globalAlpha = 1;

    // centre line
    ctx.fillStyle = line;
    ctx.fillRect(0, mid - dpr / 2, w, Math.max(1, dpr / 2));

    // time axis: subtle ticks along the bottom
    const step = tickStepFor(span);
    ctx.fillStyle = line;
    const firstTick = Math.ceil(win.start / step) * step;
    for (let t = firstTick; t <= win.end + 1e-9; t += step) {
      const x = Math.round(xOf(t));
      ctx.fillRect(x, h - 7 * dpr, Math.max(1, dpr), 7 * dpr);
    }

    // playhead
    const head = playheadRef.current;
    if (head !== null && head >= win.start && head <= win.end) {
      ctx.fillStyle = warn;
      ctx.fillRect(Math.round(xOf(head)), 0, Math.max(1, dpr * 1.5), h);
    }
  }, [peaks, cssWidth, win.start, win.end, sel.start, sel.end]);

  // keep a stable handle on the newest draw for the rAF loop below
  const drawRef = useRef(draw);
  drawRef.current = draw;

  useEffect(() => {
    draw();
  }, [draw]);

  /* ---------------- playhead animation ---------------- */

  useEffect(() => {
    if (!player.isPlaying) {
      if (playheadRef.current !== null) {
        playheadRef.current = null;
        drawRef.current();
      }
      return;
    }
    let raf = 0;
    const tick = () => {
      const el = player.audioRef.current;
      playheadRef.current = el ? el.currentTime : null;
      drawRef.current();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      playheadRef.current = null;
      drawRef.current();
    };
  }, [player.isPlaying, player.audioRef]);

  /* ---------------- dragging ---------------- */

  /** Turn a client X into a clamped boundary for `which`. */
  const applyPointer = useCallback(
    (which: HandleId, clientX: number) => {
      const el = wrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0) return;
      const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
      const t = win.start + ratio * (win.end - win.start);
      setSel((prev) =>
        which === 'start'
          ? { ...prev, start: clamp(t, win.start, Math.max(win.start, prev.end - MIN_SEC)) }
          : { ...prev, end: clamp(t, Math.min(win.end, prev.start + MIN_SEC), win.end) },
      );
    },
    [win.start, win.end],
  );

  const onPointerDown = useCallback(
    (which: HandleId) => (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      // Capture on the handle itself: every later move/up for this pointer is
      // retargeted here, so a finger that slides off the thin bar (or off the
      // canvas entirely) keeps dragging.
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* synthetic or already-released pointer — dragging still works */
      }
      setDragging(which);
      applyPointer(which, e.clientX);
    },
    [applyPointer],
  );

  const onPointerMove = useCallback(
    (which: HandleId) => (e: React.PointerEvent<HTMLDivElement>) => {
      if (draggingRef.current !== which) return;
      e.preventDefault();
      applyPointer(which, e.clientX);
    },
    [applyPointer],
  );

  const endDrag = useCallback(
    (which: HandleId) => (e: React.PointerEvent<HTMLDivElement>) => {
      if (draggingRef.current !== which) return;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* pointer already gone */
      }
      setDragging(null);
    },
    [],
  );

  /** Arrow keys nudge a handle: 10 ms, or 100 ms with Shift. */
  const onHandleKey = useCallback(
    (which: HandleId) => (e: React.KeyboardEvent<HTMLDivElement>) => {
      const dir = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
      if (dir === 0) return;
      e.preventDefault();
      const stepSec = (e.shiftKey ? 0.1 : 0.01) * dir;
      setSel((prev) =>
        which === 'start'
          ? { ...prev, start: clamp(prev.start + stepSec, win.start, Math.max(win.start, prev.end - MIN_SEC)) }
          : { ...prev, end: clamp(prev.end + stepSec, Math.min(win.end, prev.start + MIN_SEC), win.end) },
      );
    },
    [win.start, win.end],
  );

  /* ---------------- playback ---------------- */

  const playSelection = useCallback(() => {
    if (player.isPlaying && player.mode === 'selection') {
      player.stop();
      return;
    }
    // straight from the tap so the gesture still counts on iOS
    void player.play(sel.start, sel.end, 'selection');
  }, [player, sel.start, sel.end]);

  const playContext = useCallback(() => {
    if (player.isPlaying && player.mode === 'context') {
      player.stop();
      return;
    }
    void player.play(win.start, win.end, 'context');
  }, [player, win.start, win.end]);

  /* ---------------- save ---------------- */

  const closeTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    },
    [],
  );

  const save = useCallback(async () => {
    const buffer = bufferRef.current;
    if (!buffer || saving) return;
    const start = round3(sel.start);
    const end = round3(sel.end);
    if (!(end > start)) {
      setError('That selection is empty — drag the handles apart.');
      return;
    }

    setSaving(true);
    setError(null);
    player.stop();
    try {
      // Partition-preserving save: the freed or claimed audio is re-homed to
      // the adjacent tiles by the same rules as the ±0.5s buttons. The old
      // direct write left shrunk-away audio labeled as NOTHING — the one state
      // this app promises cannot exist.
      await setBoundaries(segment.id, start, end);
      setSaved(true);
      onSaved?.({ startSec: start, endSec: end });
      closeTimer.current = window.setTimeout(() => {
        closeTimer.current = null;
        onClose();
      }, SAVED_MS);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not save the new boundaries.');
      setSaving(false);
    }
  }, [saving, sel.start, sel.end, segment.id, onSaved, onClose, player]);

  const cancel = useCallback(() => {
    player.stop();
    onClose();
  }, [player, onClose]);

  /* ---------------- render ---------------- */

  const span = Math.max(1e-6, win.end - win.start);
  const pct = (t: number) => `${clamp(((t - win.start) / span) * 100, 0, 100)}%`;
  const selLen = Math.max(0, sel.end - sel.start);
  const moved = round3(sel.start) !== round3(segment.startSec) || round3(sel.end) !== round3(segment.endSec);
  const heading = (label ?? segment.heard ?? '').trim();

  const ticks = useMemo(() => {
    if (win.end <= win.start) return [] as number[];
    const step = tickStepFor(span);
    const out: number[] = [];
    for (let t = Math.ceil(win.start / step) * step; t <= win.end + 1e-9; t += step) out.push(t);
    return out;
  }, [win.start, win.end, span]);

  return (
    <section className="wt" aria-label="Adjust the clip boundaries">
      <div className="wt__head">
        <span className="wt__label">{heading || '—'}</span>
        <span className="wt__len mono">{selLen.toFixed(2)}s</span>
      </div>

      {status === 'loading' && <div className="wt__note">Decoding audio…</div>}
      {status === 'error' && (
        <div className="wt__note wt__note--err" role="alert">
          {error ?? 'Could not open this audio.'}
        </div>
      )}

      {status === 'ready' && (
        <>
          <div className="wt__stage" ref={wrapRef}>
            <canvas
              className="wt__canvas"
              ref={canvasRef}
              style={{ height: `${WAVE_H}px` }}
              aria-hidden="true"
            />

            {(['start', 'end'] as HandleId[]).map((which) => {
              const value = which === 'start' ? sel.start : sel.end;
              const active = dragging === which || focused === which;
              return (
                <div
                  key={which}
                  className={`wt-handle wt-handle--${which}${active ? ' is-active' : ''}`}
                  style={{ left: pct(value) }}
                  role="slider"
                  tabIndex={0}
                  aria-label={which === 'start' ? 'Clip start' : 'Clip end'}
                  aria-valuemin={win.start}
                  aria-valuemax={win.end}
                  aria-valuenow={value}
                  aria-valuetext={`${value.toFixed(2)} seconds`}
                  data-handle={which}
                  data-sec={value.toFixed(3)}
                  onPointerDown={onPointerDown(which)}
                  onPointerMove={onPointerMove(which)}
                  onPointerUp={endDrag(which)}
                  onPointerCancel={endDrag(which)}
                  onKeyDown={onHandleKey(which)}
                  onFocus={() => setFocused(which)}
                  onBlur={() => setFocused((f) => (f === which ? null : f))}
                >
                  <span className="wt-handle__tip mono" aria-hidden="true">
                    {value.toFixed(2)}s
                  </span>
                  <span className="wt-handle__bar" aria-hidden="true">
                    <i className="wt-handle__dot" />
                    <i className="wt-handle__dot" />
                    <i className="wt-handle__dot" />
                  </span>
                </div>
              );
            })}
          </div>

          <div className="wt__axis" aria-hidden="true">
            {ticks.map((t) => (
              <span key={t} className="wt__tick mono" style={{ left: pct(t) }}>
                {t.toFixed(2)}
              </span>
            ))}
          </div>

          <div className="wt__times mono">
            <span>{sel.start.toFixed(2)}s</span>
            <span className="wt__arrow" aria-hidden="true">
              →
            </span>
            <span>{sel.end.toFixed(2)}s</span>
            <span className="spacer" />
            <span className="wt__hint">{moved ? 'boundaries moved' : 'drag the ends'}</span>
          </div>

          <div className="wt__plays">
            <Button
              size="sm"
              className="wt__play"
              icon={player.isPlaying && player.mode === 'selection' ? '⏸' : '▶'}
              onClick={playSelection}
              disabled={saving}
            >
              Selection
            </Button>
            <Button
              size="sm"
              className="wt__play"
              icon={player.isPlaying && player.mode === 'context' ? '⏸' : '⟷'}
              onClick={playContext}
              disabled={saving}
              title="Play the whole window, context included"
            >
              Context
            </Button>
          </div>

          {(player.error || (error && status === 'ready')) && (
            <div className="wt__note wt__note--err" role="alert">
              {player.error ?? error}
            </div>
          )}

          <div className="wt__foot">
            <Button variant="ghost" size="sm" onClick={cancel} disabled={saving && !saved}>
              Cancel
            </Button>
            <span className="spacer" />
            <Button
              variant="primary"
              size="sm"
              onClick={() => void save()}
              disabled={saving || selLen < MIN_SEC}
            >
              {saved ? '✓ Saved' : saving ? 'Saving…' : 'Save cut'}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
